// Presentation-only weather + event UI. All game logic lives in
// WeatherSystem and EventSystem (driven by GameDirector).

import { WeatherType, WEATHER_DEFINITIONS, EventDefinition, EventType } from '@/config/eventsConfig'
import { useGameStore } from '@/store/gameStore'
import { getEventDefinition, resolveEventOption, dismissEvent } from '@/game/systems/EventSystem'
import { useTopModal } from '../modals/useTopModal'
import { ModalCloseButton } from './ModalCloseButton'
import { GameIcon } from '../icons/iconMap'
import { IconTile } from './CourtVision'
import {
  Zap, Landmark, Trophy, Star, Tag, Handshake, Construction, Gift,
  ClipboardCheck, Tv, Building2, MapPin, Frown, Scroll, Droplet, Volume2,
  TrendingUp, GraduationCap, Flame, ShieldAlert, type LucideIcon,
} from 'lucide-react'

// One Lucide icon per event type so events keep their visual identity
// after dropping the emoji chrome.
const EVENT_ICONS: Partial<Record<EventType, LucideIcon>> = {
  bank_ultimatum: Landmark,
  tournament_offer: Trophy,
  celebrity_visit: Star,
  equipment_sale: Tag,
  sponsorship: Handshake,
  court_damage: Construction,
  staff_bonus: Gift,
  health_inspection: ClipboardCheck,
  media_coverage: Tv,
  competitor_opens: Building2,
  terrain_deal: MapPin,
  staff_conflict: Frown,
  municipal_regulation: Scroll,
  pipe_burst: Droplet,
  noise_complaint: Volume2,
  electrical_failure: Zap,
  investor_offer: TrendingUp,
  school_proposal: GraduationCap,
  customer_surge: TrendingUp,
  social_media_viral: Flame,
  vandalism: ShieldAlert,
}

export function WeatherDisplay() {
  const weather = useGameStore(s => s.currentWeather) as WeatherType
  const weatherDef = WEATHER_DEFINITIONS[weather]

  return (
    <span className="hud-chip">
      <span style={{ color: 'var(--gold)', display: 'inline-flex' }}>
        <GameIcon domain="weather" id={weather} size={16} />
      </span>
      <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{weatherDef.name}</span>
      {weather !== 'sunny' && weather !== 'cloudy' && (
        <span style={{ color: 'var(--warning)' }}>
          {Math.round(weatherDef.outdoorModifier * 100)}%
        </span>
      )}
    </span>
  )
}

interface EventPopupProps {
  event: EventDefinition | null
  onClose: () => void
  onOption: (optionIndex: number) => void
}

export function EventPopup({ event, onClose, onOption }: EventPopupProps) {
  const money = useGameStore(s => s.money)

  if (!event) return null

  return (
    <>
      <div className="modal-backdrop" />

      <div className="modal-card">
        <ModalCloseButton onClick={onClose} />

        <div className="modal-header">
          <IconTile icon={EVENT_ICONS[event.type] ?? Zap} tone="cta" size="lg" />
          <div>
            <h3 className="text-xl font-bold" style={{ color: 'var(--text-strong)' }}>{event.title}</h3>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Evento especial</p>
          </div>
        </div>

        <div className="modal-body">
          <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{event.description}</p>
        </div>

        <div className="modal-body space-y-2" style={{ borderTop: '1px solid var(--border-default)' }}>
          {event.options.map((option, index) => {
            const cost = option.costMultiplier || 0
            const canAfford = money >= cost

            return (
              <button
                key={index}
                onClick={() => canAfford && onOption(index)}
                disabled={!canAfford && cost > 0}
                className="event-option"
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium" style={{ color: 'var(--text-strong)' }}>{option.text}</span>
                  {cost > 0 && (
                    <span style={{ color: 'var(--danger)' }}>
                      -{cost}€
                    </span>
                  )}
                  {option.rewardMultiplier !== undefined && option.rewardMultiplier > 0 && (
                    <span style={{ color: 'var(--success)' }}>
                      +{option.rewardMultiplier}€
                    </span>
                  )}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {option.effectDescription}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}

export function EventsManager() {
  const pendingEvent = useGameStore(s => s.pendingEvent)
  // Wait our turn in the modal queue (below construction/level-up/story/report)
  const top = useTopModal()
  const event = pendingEvent && top === 'event' ? getEventDefinition(pendingEvent) ?? null : null

  return (
    <EventPopup
      event={event}
      onClose={dismissEvent}
      onOption={resolveEventOption}
    />
  )
}
